"use client";

import React from "react";
import Link from "next/link";
import { useUniversalAuth, useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { MessageSquare, MessageSquarePlus } from "lucide-react";
import { Button } from "../button";
import { CommandPalette } from "../command-palette/CommandPalette";
import { CommandPaletteTrigger } from "../command-palette/CommandPaletteTrigger";
import { useCommandPaletteSearch } from "../command-palette/use-command-palette-search";
import { UserProfileMenu } from "./user-profile-menu";
import { CreateActionsMenu } from "./enhanced-quick-actions/create-actions-menu";
import { useQuickActionsNavigation } from "./enhanced-quick-actions/use-quick-actions-navigation";

export function EnhancedQuickActions() {
  const { isClientUser } = useUniversalAuth();
  const { openModal } = useGlobalModalStack();
  const searchRecords = useCommandPaletteSearch();
  const {
    isFeatureEnabled,
    canAccessCRM,
    roleCheck,
    visibleItems,
    hiddenItems,
    allItems,
    saveCustomization,
    resetCustomization,
    toggleItemVisibility,
    moveItemUp,
    moveItemDown,
    moveChildUp,
    moveChildDown,
    toggleChildVisibility,
    isLoaded,
  } = useQuickActionsNavigation();

  // For client users: a restricted quick-create "+" (ticket only) plus the
  // user profile menu. Clients get NO CRM create actions — only "New ticket"
  // (CLIENT_USER_ACCESS_SCOPE). A read-only client whose ticket creation is
  // disabled sees a disabled, explained item rather than a dead button.
  if (isClientUser) {
    return (
      <div className="flex items-center space-x-3">
        <CommandPaletteTrigger />
        <CommandPalette
          navigationItems={visibleItems}
          createGateContext={{
            canAccessCRM: false,
            isFeatureEnabled,
            isWTCEmployee: false,
            contractCreationEnabled: false,
          }}
          clientRestricted
          onSearchRecords={searchRecords}
        />

        {/* Quick Actions Group — restricted to ticket creation for clients */}
        <div className="flex items-center space-x-1 bg-muted border border-border rounded-lg p-1">
          <CreateActionsMenu
            clientRestricted
            canAccessCRM={false}
            isFeatureEnabled={isFeatureEnabled}
            isWTCEmployee={false}
          />
        </div>

        {/* User Profile Menu with Navigation Customization */}
        <UserProfileMenu
          navigationItems={allItems}
          visibleItems={visibleItems}
          hiddenItems={hiddenItems}
          onSave={saveCustomization}
          onReset={resetCustomization}
          onToggleVisibility={toggleItemVisibility}
          onMoveUp={moveItemUp}
          onMoveDown={moveItemDown}
          onMoveChildUp={moveChildUp}
          onMoveChildDown={moveChildDown}
          onToggleChildVisibility={toggleChildVisibility}
        />
      </div>
    );
  }

  // Show loading state instead of returning null to avoid hooks rule violation
  if (!isLoaded) {
    return (
      <div className="flex items-center space-x-3">
        <div className="h-9 w-9 animate-pulse bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      {/* Search + command palette. One control: it opens the palette, and it
          renders the keyboard shortcut so the shortcut is discoverable. */}
      <CommandPaletteTrigger />
      <CommandPalette
        navigationItems={visibleItems}
        createGateContext={{
          canAccessCRM,
          isFeatureEnabled,
          isWTCEmployee: roleCheck.isWTCEmployee,
          contractCreationEnabled: false,
        }}
        onSearchRecords={searchRecords}
      />

      {/* Quick Actions Group */}
      <div className="flex items-center space-x-1 bg-muted border border-border rounded-lg p-1">
        {/* Create Actions Dropdown */}
        <CreateActionsMenu
          canAccessCRM={canAccessCRM}
          isFeatureEnabled={isFeatureEnabled}
          isWTCEmployee={roleCheck.isWTCEmployee}
        />

        {/* CRM Inbox */}
        {canAccessCRM && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-8 px-3 hover:bg-accent hover:shadow-sm transition-all min-h-[44px] md:min-h-[48px] lg:min-h-[36px] md:px-4 lg:px-3"
          >
            <Link href="/crm/inbox">
              <MessageSquare className="h-4 w-4 mr-2" />
              <span className="hidden xl:inline">Inbox</span>
            </Link>
          </Button>
        )}

        {/* Admin quick action: send a text to any contact (pick recipient in the modal) */}
        {roleCheck.isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 hover:bg-accent hover:shadow-sm transition-all min-h-[44px] md:min-h-[48px] lg:min-h-[36px] md:px-4 lg:px-3"
            onClick={() => openModal(MODAL_IDS.messages.sendText, { pickContact: true })}
            title="Send a text message"
          >
            <MessageSquarePlus className="h-4 w-4 mr-2" />
            <span className="hidden xl:inline">Text</span>
          </Button>
        )}
      </div>

      {/* User Profile Menu with Navigation Customization */}
      <UserProfileMenu
        navigationItems={allItems}
        visibleItems={visibleItems}
        hiddenItems={hiddenItems}
        onSave={saveCustomization}
        onReset={resetCustomization}
        onToggleVisibility={toggleItemVisibility}
        onMoveUp={moveItemUp}
        onMoveDown={moveItemDown}
        onMoveChildUp={moveChildUp}
        onMoveChildDown={moveChildDown}
        onToggleChildVisibility={toggleChildVisibility}
      />
    </div>
  );
}
